import { client } from "@/hera-api/client/client.gen";
import type {
  LicenseCommentsResponseSchema,
  LicensePaymentsResponseSchema,
  NonRegistrantLicenseFilterValuesResponseSchema,
  NonRegistrantLicenseMapPinsResponseSchema,
  RegistrantLicenseListResponseSchema,
  RegistrantLicenseListStatsResponseSchema,
} from "@/hera-api/client/types.gen";

export type OmtAmsAccessResponse = {
  email: string;
  party_listed: boolean;
  license_count: number;
  party_name?: string | null;
  company_name?: string | null;
  party_id?: string | null;
  account_type?: "PRIMARY" | "SECONDARY" | null;
  is_admin?: boolean;
  can_manage_users?: boolean;
  primary_id?: string | null;
  primary_id_pending?: string | null;
};

export type OmtAmsImpersonationParty = {
  email: string;
  name: string | null;
  company_name: string | null;
};

export type OmtAmsAccountResponse = {
  email: string;
  name: string | null;
  party_listed: boolean;
  party_id?: string | null;
  account_type?: "PRIMARY" | "SECONDARY" | null;
  is_admin?: boolean;
  can_manage_users?: boolean;
  primary_id?: string | null;
  primary_id_pending?: string | null;
  company_name?: string | null;
};

export type OmtAmsAccountUser = {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  account_type: "PRIMARY" | "SECONDARY" | null;
  status: "active" | "pending" | "disabled";
  is_admin: boolean;
  company_name?: string | null;
};

export type OmtAmsPendingInviteInfo = {
  primary_party_id: string;
  primary_party_name: string | null;
  primary_party_company_name: string | null;
};

async function getOrThrow<T>(
  url: string,
  query?: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await client.get({
    url: url as never,
    query: query as never,
    throwOnError: false,
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
  });
  if (error || data === undefined) {
    throw error ?? new Error(`Failed to load ${url}`);
  }
  return data as T;
}

async function requestOrThrow<T>(
  method: "post" | "patch" | "delete",
  url: string,
  body?: Record<string, unknown>,
): Promise<T | undefined> {
  const { data, error } = await client[method]({
    url: url as never,
    body: body as never,
    throwOnError: false,
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
    ],
  });
  if (error) {
    throw error;
  }
  return data as T | undefined;
}

export async function getOmtAmsAccess(): Promise<OmtAmsAccessResponse> {
  return getOrThrow<OmtAmsAccessResponse>("/omt-ams/access");
}

export async function searchOmtAmsPartiesForImpersonation(query: {
  q: string;
  limit?: number;
}): Promise<{ parties: OmtAmsImpersonationParty[] }> {
  return getOrThrow<{ parties: OmtAmsImpersonationParty[] }>(
    "/omt-ams/impersonation/search-parties",
    query,
  );
}

export async function getOmtAmsAccount(): Promise<OmtAmsAccountResponse> {
  return getOrThrow<OmtAmsAccountResponse>("/omt-ams/account");
}

export async function getOmtAmsLicenses(
  query: Record<string, unknown>,
): Promise<RegistrantLicenseListResponseSchema> {
  return getOrThrow<RegistrantLicenseListResponseSchema>(
    "/omt-ams/licenses",
    query,
  );
}

export async function getOmtAmsLicenseStats(
  query: Record<string, unknown>,
): Promise<RegistrantLicenseListStatsResponseSchema> {
  return getOrThrow<RegistrantLicenseListStatsResponseSchema>(
    "/omt-ams/licenses/stats",
    query,
  );
}

export async function getOmtAmsLicenseFilterValues(
  query: Record<string, unknown>,
): Promise<NonRegistrantLicenseFilterValuesResponseSchema> {
  return getOrThrow<NonRegistrantLicenseFilterValuesResponseSchema>(
    "/omt-ams/licenses/filter-values",
    query,
  );
}

export async function getOmtAmsLicenseMapPins(
  query: Record<string, unknown>,
): Promise<NonRegistrantLicenseMapPinsResponseSchema> {
  return getOrThrow<NonRegistrantLicenseMapPinsResponseSchema>(
    "/omt-ams/licenses/map-pins",
    query,
  );
}

export async function getOmtAmsLicenseComments(
  licenseId: string,
): Promise<LicenseCommentsResponseSchema> {
  return getOrThrow<LicenseCommentsResponseSchema>(
    `/omt-ams/licenses/${licenseId}/comments`,
  );
}

export async function getOmtAmsLicensePayments(
  licenseId: string,
): Promise<LicensePaymentsResponseSchema> {
  return getOrThrow<LicensePaymentsResponseSchema>(
    `/omt-ams/licenses/${licenseId}/payments`,
  );
}

export async function getOmtAmsAccountUsers(): Promise<{
  users: OmtAmsAccountUser[];
}> {
  return getOrThrow<{ users: OmtAmsAccountUser[] }>("/omt-ams/account/users");
}

export async function addOmtAmsSecondaryUser(email: string): Promise<{
  email: string;
}> {
  const data = await requestOrThrow<{ email: string }>(
    "post",
    "/omt-ams/account/users",
    { email },
  );
  return data ?? { email };
}

export async function cancelOmtAmsSecondaryUserInvite(
  userId: string,
): Promise<void> {
  await requestOrThrow("delete", `/omt-ams/account/users/${userId}`);
}

export async function disableOmtAmsSecondaryUser(
  userId: string,
): Promise<void> {
  await requestOrThrow("delete", `/omt-ams/account/users/${userId}/disable`);
}

export async function reenableOmtAmsSecondaryUser(
  userId: string,
): Promise<void> {
  await requestOrThrow("post", `/omt-ams/account/users/${userId}/reenable`);
}

export async function updateOmtAmsAccountUserAdmin(
  userId: string,
  isAdmin: boolean,
): Promise<void> {
  await requestOrThrow("patch", `/omt-ams/account/users/${userId}/admin`, {
    is_admin: isAdmin,
  });
}

export async function getOmtAmsPendingInviteInfo(): Promise<OmtAmsPendingInviteInfo> {
  return getOrThrow<OmtAmsPendingInviteInfo>("/omt-ams/account/pending-invite");
}

export async function acceptOmtAmsPendingInvite(): Promise<void> {
  await requestOrThrow("post", "/omt-ams/account/accept-invite");
}

export async function rejectOmtAmsPendingInvite(): Promise<void> {
  await requestOrThrow("post", "/omt-ams/account/reject-invite");
}
